import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import styled from 'styled-components';

import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';
import { useLanguage } from '../../../Context/LanguageContext';

import FetchModule from '../../Share/Network/FetchModule';

import SalesCalendarWeekRow from './SalesCalendarWeekRow';

export default function SalesCalendarBox(props) {
  const {
    settlementCycle,
    settlements,
    settledMerchantUids,
    standardDate,
    yearSales,
    isOpenPartnerGymSales,
    setIsYearLoading
  } = props;

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

  const [monthData, setMonthData] = useState([]);
  const [monthSalesPrice, setMonthSalesPrice] = useState(0);
  const [todaySalesPrice, setTodaySalesPrice] = useState(0);
  const [expectedSettlementPrice, setExpectedSettlementPrice] = useState(0);
  const [settlementPrice, setSettlementPrice] = useState(0);
  const [holidays, setHolidays] = useState(new Set());
  const [selectedDate, setSelectedDate] = useState(null);

  const history = useHistory();

  const salesDailyRef = useRef(null);

  const firstWeek = standardDate.clone().startOf('month').week();
  const lastWeek = standardDate.clone().endOf('month').week() === 1 ? 53 : standardDate.clone().endOf('month').week();

  // 모달 바깥 클릭 시 닫기 //
  useEffect(() => {
    if (!selectedDate) return;

    function onClickOutside(e) {
      if (salesDailyRef.current && !salesDailyRef.current.contains(e.target)) {
        setSelectedDate(null);
      }
    }

    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [selectedDate]);

  // 공휴일 데이터 조회 //
  useEffect(() => {
    const startDate = standardDate.clone().startOf('month').subtract(10, 'days').format('YYYY-MM-DD');
    const endDate = standardDate.clone().endOf('month').add(10, 'days').format('YYYY-MM-DD');

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.getDataVersion2('holiday?startDate=' + startDate + '&endDate=' + endDate, 'GET');

        if (responseData.status === 200) {
          const holidaySet = new Set();

          for (const holiday of responseData.data.holidays) {
            holidaySet.add(moment(holiday.holidayDate).format('YYYY-MM-DD'));
          }

          setHolidays(holidaySet);
        }
        else if (responseData.status === 401) {
          history.push('/');
        }
      }
    )();
  }, [standardDate]);

  // 주말/공휴일 체크 //
  function isOffDay(m) {
    const dow = m.day(); // 0=일, 6=토
    if (dow === 0 || dow === 6) return true; // 주말
    return holidays.has(m.format('YYYY-MM-DD')); // 공휴일
  }

  // 다음 영업일 계산 //
  function nextBusinessDay(m) {
    const d = m.clone();
    while (isOffDay(d)) {
      d.add(1, 'day');
    }
    return d;
  }

  // 오늘 기준으로 몇 일 뒤가 영업일인지 계산 //
  function daysUntilBusinessDay(m) {
    let d = m.clone();
    let days = 0;
    
    while (isOffDay(d)) {
      d.add(1, 'day');
      days++;
    }

    return days;
  }

  // 달력 만들기 //
  useEffect(() => {
    let newDateList = [];
    let linkopayFeeRate = 0.035;
    
    for (let week = firstWeek; week <= lastWeek; week++) {
      for (let i = 0; i < 7; i++) {
        let days = standardDate.clone().startOf('year').week(week).startOf('week').add(i, 'day');
        
        let newData = {
          date: days,
          settlementPrice: 0,
          salesPrice: 0,
          sales: [],
          settlementStatus: 'SCHEDULED'
        }

        // - REQUESTED: 지급이 요청되었지만 아직 처리되지 않은 상태입니다. REQUESTED 상태일 때만 지급대행 요청을 취소할 수 있습니다.

        // - IN_PROGRESS: 지급을 처리하고 있는 상태입니다.

        // - COMPLETED: 셀러에 지급이 완료된 상태입니다.

        // - FAILED: 지급 요청이 실패한 상태입니다.

        // - CANCELED: 지급 요청이 취소된 상태입니다.
        newDateList.push(newData);
      } 
    }

    // 이미 서버에서 정산 처리된 merchantUid Set (O(1) lookup) //
    const settledUidSet = new Set(settledMerchantUids || []);

    for (let i = 0; i < newDateList.length; i++) {
      for (let j = 0; j < yearSales.length; j++) {
        if (newDateList[i].date.clone().format('YYYY.MM.DD') === moment(yearSales[j].approvalDate).format('YYYY.MM.DD')) {
          newDateList[i].salesPrice += (yearSales[j].totalPrice);
          newDateList[i].sales.push(yearSales[j]);
        }

        if (newDateList[i].date.clone().subtract(settlementCycle, 'days').format('YYYY.MM.DD') === moment(yearSales[j].approvalDate).format('YYYY.MM.DD')
          && moment('2025-09-01').isBefore(moment(yearSales[j].approvalDate))
          && yearSales[j].cardPrice > 0
          && (yearSales[j].merchantUid.split('-')[2] === '1' || yearSales[j].merchantUid.split('-')[2] === '2')
          && !settledUidSet.has(yearSales[j].merchantUid)) {
            let feeAmount = Math.floor(yearSales[j].cardPrice * linkopayFeeRate);
            
            // 정산일이 휴일/공휴일인 경우 //
            if (isOffDay(moment(newDateList[i].date))) {
              let days = daysUntilBusinessDay(moment(newDateList[i].date));

              // 이번달 날짜 데이터 안에 들어있는 경우 //
              if (newDateList.length > (i + days)) newDateList[i + days].settlementPrice += (yearSales[j].cardPrice - feeAmount);
            }
            // 정산일이 영업일인 경우 //
            else {
              newDateList[i].settlementPrice += (yearSales[j].cardPrice - feeAmount);
            }
        }
      }
    }
    
    let newMonthSalesPrice = 0;
    let newTodaySalesPrice = 0;
    
    for (let i = 0; i < yearSales.length; i++) {
      // 해당월 매출액 합산 //
      if (standardDate.clone().format('YYYY.MM') === moment(yearSales[i].approvalDate).format('YYYY.MM')) {
        // newMonthSalesPrice += (yearSales[i].cardPrice + yearSales[i].cashPrice);
        newMonthSalesPrice += (yearSales[i].totalPrice);
      }
      // 금일 매출액 합산 //
      if (moment().clone().format('YYYY.MM.DD') === moment(yearSales[i].approvalDate).format('YYYY.MM.DD')) {
        // newTodaySalesPrice += (yearSales[i].cardPrice + yearSales[i].cashPrice);
        newTodaySalesPrice += (yearSales[i].totalPrice);
      }
    }
  
    // 서버 정산 데이터로 해당 날짜의 프론트 예측 덮어쓰기 //
    for (let i = 0; i < newDateList.length; i++) {
      for (let j = 0; j < settlements.length; j++) {
        if (newDateList[i].date.clone().format('YYYY.MM.DD') === moment(settlements[j].settledDate).format('YYYY.MM.DD')) {
          newDateList[i].settlementPrice = settlements[j].settlementAmount;
          newDateList[i].settlementStatus = settlements[j].status;
          break;
        }
      }
    }

    let totalSettlementPrice = 0;
    let totalScheduledSettlementPrice = 0;

    for (let i = 0; i < newDateList.length; i++) {
      if (newDateList[i].date.clone().format('YYYY.MM') === standardDate.clone().format('YYYY.MM'))
      if (newDateList[i].settlementStatus === 'COMPLETED') totalSettlementPrice += newDateList[i].settlementPrice;
      else totalScheduledSettlementPrice += newDateList[i].settlementPrice;
    }

    setMonthSalesPrice(newMonthSalesPrice);
    setTodaySalesPrice(newTodaySalesPrice);
    setMonthData(newDateList);
    setSettlementPrice(totalSettlementPrice);
    setExpectedSettlementPrice(totalScheduledSettlementPrice);

    setTimeout(() => (
      setIsYearLoading(false)
    ), 1000)
  }, [standardDate, firstWeek, lastWeek, yearSales, settlementCycle, settlements, settledMerchantUids, holidays])

  // 주 단위 데이터 분리 //
  const weeklyData = [];

  for (let i = 0; i < monthData.length; i += 7) {
    weeklyData.push(monthData.slice(i, i + 7));
  }

  // 날짜 클릭 시 해당 날짜 매출 데이터 필터링 //
  function onClickDateBox(date) {
    if (selectedDate && selectedDate.format('YYYY.MM.DD') === date.format('YYYY.MM.DD')) {
      setSelectedDate(null);
      return;
    }
    setSelectedDate(date.clone());
  }

  // 매출 항목 클릭 시 회원 상세 이동 //
  function onClickSalesItem(data) {
    if (data.memberId === undefined) {
      alert(t('statistics.sales.alertNotRegistered'));
      return;
    }
    window.open('/member/valid/detail/' + data.memberId, '_blank');
  }

  return (
    <TopLevelWrapper>
      <CalendarBox $isDark={isDark}>
        <TitleWrapper>
          <Text fontFamily='NotoSansKR-Bold' size={20} color={theme.text} cursor='default'>{standardDate.format(t('statistics.dateFormatMonthShort'))}</Text>
        </TitleWrapper>
        <InfoComponentBoxWrapper>
          <InfoComponentBox>
            <Text fontFamily='NotoSansKR-Medium' size={14} color={theme.text} cursor='default'>{standardDate.format('M') + t('statistics.salesCalendar.monthlySales')}</Text>
            <Text fontFamily='NotoSansKR-Bold' size={17} color={theme.text} cursor='default'>{monthSalesPrice.toLocaleString('ko-KR') + ' ' + t('statistics.won')}</Text>
          </InfoComponentBox>
          <InfoComponentBox>
            <Text fontFamily='NotoSansKR-Medium' size={14} color={theme.text} cursor='default'>{standardDate.format('M') + t('statistics.salesCalendar.monthlyLinkoPaySettlement')}</Text>
            <Text fontFamily='NotoSansKR-Bold' size={17} color={theme.text} cursor='default'>{settlementPrice.toLocaleString('ko-KR') + ' ' + t('statistics.won')}</Text>
          </InfoComponentBox>
          {/*<InfoComponentBox>
            <Text fontFamily='NotoSansKR-Medium' size={14} color={theme.text} cursor='default'>{t('statistics.salesCalendar.todaySales')}</Text>
            <Text fontFamily='NotoSansKR-Bold' size={17} color={theme.text} cursor='default'>{todaySalesPrice.toLocaleString('ko-KR') + ' ' + t('statistics.won')}</Text>
          </InfoComponentBox>*/}
          <InfoComponentBox>
            <Text fontFamily='NotoSansKR-Medium' size={14} color={theme.text} cursor='default'>{t('statistics.salesCalendar.linkoPayExpectedSettlement')}</Text>
            <Text fontFamily='NotoSansKR-Bold' size={17} color={theme.text} cursor='default'>{expectedSettlementPrice.toLocaleString('ko-KR') + ' ' + t('statistics.won')}</Text>
          </InfoComponentBox>
        </InfoComponentBoxWrapper>
        <TotalExplanationWrapper>
          <ExplanationWrapper>
            <ColorBox backgroundColor={theme.text}/>
            <Text fontFamily='NotoSansKR-Regular' size={14} color={theme.text} cursor='default'>{t('statistics.salesCalendar.salesAmount')}</Text>
          </ExplanationWrapper>
          <ExplanationWrapper>
            <ColorBox backgroundColor={theme.subKey}/>
            <Text fontFamily='NotoSansKR-Regular' size={14} color={theme.text} cursor='default'>{t('statistics.salesCalendar.linkoPaySettlement')}</Text>
          </ExplanationWrapper>
          {/*<ExplanationWrapper>
            <ColorBox backgroundColor='#B5B5B5'/>
            <Text fontFamily='NotoSansKR-Regular' size={14} color={theme.text} cursor='default'>{t('statistics.salesCalendar.expectedSettlement')}</Text>
          </ExplanationWrapper>*/}
        </TotalExplanationWrapper>
        <WeekWrapper>
          <WeekBox>
            <Text fontFamily='NotoSansKR-Regular' size={14} color={theme.text} cursor='default'>{t('statistics.salesCalendar.sun')}</Text>
          </WeekBox>
          <WeekBox>
            <Text fontFamily='NotoSansKR-Regular' size={14} color={theme.text} cursor='default'>{t('statistics.salesCalendar.mon')}</Text>
          </WeekBox>
          <WeekBox>
            <Text fontFamily='NotoSansKR-Regular' size={14} color={theme.text} cursor='default'>{t('statistics.salesCalendar.tue')}</Text>
          </WeekBox>
          <WeekBox>
            <Text fontFamily='NotoSansKR-Regular' size={14} color={theme.text} cursor='default'>{t('statistics.salesCalendar.wed')}</Text>
          </WeekBox>
          <WeekBox>
            <Text fontFamily='NotoSansKR-Regular' size={14} color={theme.text} cursor='default'>{t('statistics.salesCalendar.thu')}</Text>
          </WeekBox>
          <WeekBox>
            <Text fontFamily='NotoSansKR-Regular' size={14} color={theme.text} cursor='default'>{t('statistics.salesCalendar.fri')}</Text>
          </WeekBox>
          <WeekBox>
            <Text fontFamily='NotoSansKR-Regular' size={14} color={theme.text} cursor='default'>{t('statistics.salesCalendar.sat')}</Text>
          </WeekBox>
        </WeekWrapper>
        <DateBoxWrapper isNotOpen={!isOpenPartnerGymSales}>
          {
            !isOpenPartnerGymSales && <NotOpenOpacityBox/>
          }
          {
            !isOpenPartnerGymSales &&
            <NotOpenBox>
              <Text fontFamily='NotoSansKR-Bold' fontSize={15} color={theme.subText} cursor='default'>{t('statistics.partnerGymPrivate')}</Text>
              <Text fontFamily='NotoSansKR-Regular' fontSize={15} color={theme.subText} cursor='default'>{t('statistics.partnerGymPrivateDesc')}</Text>
            </NotOpenBox>
          }
          {
            weeklyData.map((week, weekIndex) => (
              <SalesCalendarWeekRow
                key={'week-' + weekIndex}
                weekData={week}
                standardDate={standardDate}
                selectedDate={selectedDate}
                salesDailyRef={salesDailyRef}
                onClickDateBox={onClickDateBox}
                onClickClose={() => setSelectedDate(null)}
                onClickSalesItem={onClickSalesItem} />
            ))
          }
        </DateBoxWrapper>
      </CalendarBox>
      <DivisionLine/>
    </TopLevelWrapper>
  )
}

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;
`;

const CalendarBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  padding: 28px 52px 28px 52px;
  margin-top: 32px;

  width: 916px;
  // height: 779px;
  min-height: 660px;

  border-radius: 20px;
  background-color: ${props => props.theme.subBackground};
  box-shadow: ${props => props.$isDark ? 'none' : '0px -20px 30px 0px rgba(0, 0, 0, 0.02)'};
`;

const DivisionLine = styled.div`
  margin: 32px 0px 32px 0px;

  width: 95%;
  height: 1px;

  background-color: ${props => props.theme.line};
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;
`;

const InfoComponentBoxWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  margin-top: 30px;

  width: 100%;

  gap: 24px;
`;

const InfoComponentBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;

  gap: 4px;
`;

const TotalExplanationWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  margin-top: 50px;

  width: 100%;

  gap: 16px;
`;

const ExplanationWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 8px
`;

const ColorBox = styled.div`
  width: 13px;
  height: 13px;

  border-radius: 13px;
  background-color: ${props => props.backgroundColor};
`;

const WeekWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  margin-top: 26px;

  width: 100%;

  gap: 8px;
`;

const WeekBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  width: 131px;
`;

const DateBoxWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;
`;

const NotOpenOpacityBox = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 100%;

  opacity: 0.8;
  background-color: ${props => props.theme.background};
`;

const NotOpenBox = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 100%;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.size}px;

  color: ${props => props.color};

  margin: 0 0 0 0;

  &:hover {
    cursor: ${props => props.cursor};
  }
`;
